import {
  isGroup as isGroupCheck,
  timeOfDaySchema,
  groupStatusSchema,
  type IActivityOccurrenceOrGroupWithRequiredPatient,
} from "@medoma/models/activities";
import styles from "./ActivityCalendarItem.module.scss";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { Text } from "@medoma/components/Text/Text";
import { formattedTime } from "@/components/Time/timeUtils";
import { UnstyledLink } from "@medoma/components/Link/Link";
import { useParams } from "react-router";
import clsx from "clsx";

type IActivityCalendarItem = {
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroupWithRequiredPatient;
};

const Title = ({ activityOccurrenceOrGroup }: IActivityCalendarItem) => {
  const isGroup = isGroupCheck(activityOccurrenceOrGroup);
  const isCancelled =
    isGroup &&
    activityOccurrenceOrGroup.visitStatus === groupStatusSchema.enum.cancelled;
  if (isGroup) {
    return (
      <div className={styles.groupTitle}>
        <Text
          element="span"
          size="smallest"
          className={clsx(isCancelled && styles.cancelledTitle)}
        >
          {activityOccurrenceOrGroup.occurrences
            .map((occurrence) => occurrence.title)
            .join(", ")}
        </Text>
        <div className={styles.occurrenceCount}>
          {activityOccurrenceOrGroup.occurrences.length}
        </div>
      </div>
    );
  } else {
    return (
      <Text element="span" size="smallest">
        {activityOccurrenceOrGroup.title}
      </Text>
    );
  }
};

const ActivityCalendarItem = ({
  activityOccurrenceOrGroup,
}: IActivityCalendarItem) => {
  const isGroup = isGroupCheck(activityOccurrenceOrGroup);
  const params = useParams();

  const activeItem = isGroup ? params.groupId : params.occurrenceId;
  const activeItemActivityId = params.activityId;

  const { category, start, timeOfDay } = activityOccurrenceOrGroup;
  const isAnyTimeOfDay = timeOfDay === timeOfDaySchema.enum.Any;

  const isActiveItem = isGroup
    ? activeItem === activityOccurrenceOrGroup.id
    : activeItem === activityOccurrenceOrGroup.id &&
      activeItemActivityId === activityOccurrenceOrGroup.activityId;
  const isGroupWithActiveSeriesItem =
    isGroup &&
    activeItemActivityId !== undefined &&
    activityOccurrenceOrGroup.occurrences.some(
      (occurrence) => occurrence.activityId === activeItemActivityId,
    );
  const isActiveSeriesItem =
    !isGroup && activeItemActivityId === activityOccurrenceOrGroup.activityId;

  return (
    <UnstyledLink
      to={
        isGroup
          ? `occurrence-groups/${activityOccurrenceOrGroup.id}`
          : `${activityOccurrenceOrGroup.activityId}/occurrences/${activityOccurrenceOrGroup.id}`
      }
    >
      <div
        className={clsx([
          styles.activityCalendarItem,
          (isActiveSeriesItem || isGroupWithActiveSeriesItem) &&
            styles.activeSeriesItem,
          isActiveItem && styles.activeItem,
        ])}
      >
        <CategoryIcon size="tiny" category={category} />
        {isAnyTimeOfDay ? null : (
          <Text element="span" size="smallest">
            {formattedTime(start)}
          </Text>
        )}
        <Title activityOccurrenceOrGroup={activityOccurrenceOrGroup} />
      </div>
    </UnstyledLink>
  );
};

const ActivityCalendarItemPlaceholder = () => (
  <div className={styles.activityCalendarItemPlaceholder} />
);

export { ActivityCalendarItem, ActivityCalendarItemPlaceholder };
